import React, { useEffect, useState } from "react"
import styled from "styled-components"
import PropTypes from "prop-types"
import { graphql } from "gatsby"
import Img from "gatsby-image"
import { FacebookShareButton, LinkedinShareButton } from "react-share"
import SmoothScroll from "smooth-scroll"
import shortid from "shortid"
import Layout from "../components/layout/layout"
import Content, { HTMLContent, Post } from "../components/blog/content"
import * as authors from "../data/authors"
import AboutAuthor from "../components/blog/aboutAuthor"
import linkedinShare from "../images/icons/socials/linked-in-share.svg"
import facebookShare from "../images/icons/socials/facebook-share.svg"
import SEO from "../components/layout/seo"

const StyledArticle = styled.article`
  color: #fff;
  padding: 95px 16px 128px;
  background-color: #000;
  @media (min-width: 768px) {
    padding: 160px 16px 64px;
  }
  @media (min-width: 1024px) {
    padding: 160px 16px 0;
  }
`

const Subtitle = styled.p`
  font-size: 24px;
  line-height: 1.08;
  letter-spacing: 0.3px;
  font-family: "Made Bold", sans-serif;
  margin: ${props => props.author && "40px 0 12px"};
  &.spis {
    display: none;
  }
  @media (min-width: 768px) {
    margin: ${props => props.author && "120px 0 20px"};
  }
  @media (min-width: 1440px) {
    &.spis {
      display: block;
    }
  }
`

const StyledArticleContainer = styled.div`
  @media (min-width: 1440px) {
    display: flex;
    flex-direction: row-reverse;
    justify-content: space-between;
    & aside {
      width: 330px;
    }
    & ${Post} {
      width: calc(100% - 330px - 70px);
    }
  }
`

const Container = styled.div`
  position: relative;
  z-index: 20;
  @media (min-width: 768px) {
    max-width: 576px;
    margin: 0 auto;
    background-color: #000;
    padding: 35px;
    transform: translateY(-35px);
    border-radius: 8px;
  }
  @media (min-width: 1024px) {
    max-width: 768px;
    padding: 70px;
    transform: translateY(-70px);
  }
  @media (min-width: 1200px) {
    max-width: 1024px;
  }
  @media (min-width: 1440px) {
    max-width: 1230px;
  }
  @media (min-width: 1500px) {
    max-width: 1310px;
  }
`

const FeaturedImg = styled(Img)`
  border-radius: 8px;
  margin: 0 auto;
  position: relative;
  z-index: 20;
  max-width: 1170px;
  width: 100%;
  max-height: 110px;
  @media (min-width: 768px) {
    width: 100vw;
    transform: translateX(-16px);
    border-radius: 0;
    max-height: 370px;
    max-width: 1570px;
  }
  @media (min-width: 1550px) {
    transform: translateX(0);
  }
`
const InfoBox = styled.div`
  margin: 16px 0;
  display: flex;
  flex-direction: column;
  @media (min-width: 768px) {
    margin: 0 0 40px;
  }
  @media (min-width: 1440px) {
    width: calc(100% - 330px - 70px);
  }
`

const Title = styled.h1`
  font-family: "Made Evo Bold", sans-serif;
  font-size: 28px;
  line-height: 1.29;
  letter-spacing: 0.2px;
  color: #fff;
  @media (min-width: 768px) {
    font-size: 48px;
    line-height: 1.25;
    letter-spacing: 0.6px;
  }
`

const AuthorAndDate = styled.div`
  display: flex;
  p {
    color: #808080;
    font-family: "Made Light", sans-serif;
    letter-spacing: 0.4px;
    font-size: 16px;
  }
`

const ContentTable = styled.div`
  display: none;
  background-color: #151515;
  padding: 20px 30px;
  border-radius: 8px;
  margin-top: 20px;
  & ol {
    padding-left: 15px;
  }
  & ol li {
    font-size: 20px;
    letter-spacing: 0.3px;
    line-height: normal;
    margin-top: 24px;
    &:first-child {
      margin-top: 0;
    }
    & a {
      font-size: 20px;
      letter-spacing: 0.3px;
      line-height: normal;
      color: #f9d6c1;
      display: block;
      text-decoration: none;
      &:hover {
        text-decoration: underline;
      }
    }
  }
  @media (min-width: 1440px) {
    display: block;
  }
`

const Socials = styled.div`
  margin-top: 32px;
  width: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  button {
    margin-right: 16px;
    &:hover {
      cursor: pointer;
    }
    &:last-child {
      margin-right: 0;
    }
  }
  @media (min-width: 768px) {
    margin: 61px auto 0;
    button {
      margin-right: 24px;
    }
  }
`

const Share = styled.p`
  font-family: "Made Medium", sans-serif;
  font-size: 14px;
  letter-spacing: 0.3px;
  margin-right: 36px;
  opacity: 0.5;
  @media (min-width: 768px) {
    font-size: 16px;
    margin-right: 48px;
  }
`

export const BlogPostTemplate = ({
  content,
  contentComponent,
  title,
  image,
  date,
  author,
  url,
}) => {
  const PostContent = Content || contentComponent

  const [titles, setTitles] = useState(null)

  useEffect(() => {
    const h2Titles = document.querySelectorAll(".blog-post-wrapper h2")
    const arrh2Titles = [...h2Titles]
    const arr = []
    for (let i = 0; i < arrh2Titles.length; i++) {
      arr.push(shortid.generate())
    }

    if (typeof window !== "undefined") {
      // eslint-disable-next-line no-unused-vars
      const scroll = new SmoothScroll('a[href*="#"]', {
        offset: 180,
        speed: 300,
        durationMax: 500,
        speedAsDuration: false,
      })
    }

    arrh2Titles.forEach((h2, i) => {
      if (!h2.id) {
        h2.setAttribute("id", arr[i])
      }
    })

    if (!titles) {
      const list = arrh2Titles.map(({ innerText }, i) => {
        return (
          <li key={innerText}>
            <a href={`#${arr[i]}`}>{innerText}</a>
          </li>
        )
      })
      setTitles(list)
    }
  })

  const NisMediaURL = `https://nismedia.pl${url}`

  return (
    <StyledArticle>
      <FeaturedImg fluid={image.fluid} fadeIn alt="hero-article-cover" />
      <Container>
        <InfoBox>
          <Title>{title}</Title>
          <AuthorAndDate>
            <p>Opublikowany: {date}</p>
          </AuthorAndDate>
        </InfoBox>
        <StyledArticleContainer>
          <aside>
            <Subtitle className="spis">Spis treści</Subtitle>
            <ContentTable>
              <ol>{titles}</ol>
            </ContentTable>
          </aside>
          <PostContent content={content} />
        </StyledArticleContainer>
        <Subtitle author>O autorze</Subtitle>
        <AboutAuthor author={author} />
        <Socials>
          <Share>Udostępnij</Share>
          <FacebookShareButton url={NisMediaURL}>
            <img
              src={facebookShare}
              alt="facebook icon"
              title="Share on Facebook"
            />
          </FacebookShareButton>
          <LinkedinShareButton url={NisMediaURL}>
            <img
              src={linkedinShare}
              alt="linkedin icon"
              title="Share on LinkedIn"
            />
          </LinkedinShareButton>
        </Socials>
      </Container>
    </StyledArticle>
  )
}

BlogPostTemplate.propTypes = {
  content: PropTypes.node.isRequired,
  contentComponent: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  image: PropTypes.oneOfType([PropTypes.object]).isRequired,
  date: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  author: PropTypes.arrayOf(
    PropTypes.shape({
      authorName: PropTypes.string.isRequired,
      authorJob: PropTypes.string.isRequired,
      authorDesc: PropTypes.string.isRequired,
      linkedInProfile: PropTypes.string.isRequired,
      mail: PropTypes.string.isRequired,
      avatar: PropTypes.string.isRequired,
    })
  ).isRequired,
}

const BlogPost = ({ data }) => {
  const { markdownRemark: post } = data

  const allAuthorsArr = Object.values(authors)
  const authorData = allAuthorsArr.filter(item =>
    item.authorName.includes(post.frontmatter.author)
  )

  const imgSocial = post.frontmatter.featuredimage.childImageSharp.fluid.src

  return (
    <Layout blackNav hideInstaPosts>
      <SEO
        title={post.frontmatter.title}
        description={post.frontmatter.description}
        imgMeta={imgSocial}
        canonical={post.fields.slug}
      />
      <BlogPostTemplate
        content={post.html}
        contentComponent={HTMLContent}
        description={post.frontmatter.description}
        image={post.frontmatter.featuredimage.childImageSharp}
        date={post.frontmatter.date}
        author={authorData}
        url={post.fields.slug}
        title={post.frontmatter.title}
      />
    </Layout>
  )
}

BlogPost.propTypes = {
  data: PropTypes.shape({
    markdownRemark: PropTypes.object.isRequired,
  }).isRequired,
}

export default BlogPost

export const pageQuery = graphql`
  query BlogPostByID($id: String!) {
    markdownRemark(id: { eq: $id }) {
      id
      html
      frontmatter {
        date(formatString: "LL", locale: "pl-PL")
        title
        description
        featuredimage {
          childImageSharp {
            fluid(maxWidth: 1170) {
              ...GatsbyImageSharpFluid_withWebp
            }
          }
        }
        author
      }
      fields {
        slug
      }
    }
  }
`
